import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import useDataContext from 'data/context/data';

import { inchesToCm, lengthLabel, railPriceGroups } from '../../lib/displayPrices.mjs';
import { money } from '../../lib/landingPrice';
import { ProductDetailLink } from '../pageLinks';
import PdpIcon from '../product/pdpIcon';
import { Picture, Responsive, imageShape } from '../landing/shared';

const RAILS_ID = 'wooden-rails';

const Length = ({ inches }) => (
  <>
    <span className="dsp-rail-in">{`${inches} in`}</span>
    <span className="dsp-rail-cm">{`${inchesToCm(inches)} cm`}</span>
  </>
);

Length.propTypes = { inches: PropTypes.number.isRequired };

// Rail length per print size and orientation (the Wood Poster Rails FAQ), then the rail prices
// by length from the catalog.
const RailGuide = ({ rows, image, track }) => {
  const dataContext = useDataContext();
  const rails = dataContext.getProduct(RAILS_ID);
  const priceGroups = rails ? railPriceGroups(rails.options) : [];

  return (
    <section className="lnd-section dsp-rails">
      <Container className="dsp-rails-inner">
        <div className="dsp-rails-photo">
          <Picture image={image} sizes="(min-width: 992px) 480px, 100vw" />
        </div>
        <div>
          <p className="lnd-eyebrow">Wood Poster Rails</p>
          <h2 className="lnd-h2">Which rails fit your print</h2>
          <p className="dsp-rails-lead">
            Pick the rail length by print size and by which way the print hangs.
          </p>

          <table className="dsp-rail-table">
            <thead>
              <tr>
                <th scope="col">Print</th>
                <th scope="col">Portrait</th>
                <th scope="col">Landscape</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name}>
                  <th scope="row">
                    <span className="dsp-rail-print">
                      <Responsive desktop={row.name} phone={row.short} />
                    </span>
                    <span className="dsp-rail-cm">
                      {`${inchesToCm(row.size[0])} × ${inchesToCm(row.size[1])} cm`}
                    </span>
                  </th>
                  <td>
                    <Length inches={row.portrait} />
                  </td>
                  <td>
                    <Length inches={row.landscape} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {priceGroups.length > 0 && (
            <div className="dsp-rail-prices">
              <p className="dsp-rail-prices-title">Rail prices by length</p>
              <p>
                {priceGroups.map(({ lengths, price }, index) => (
                  <React.Fragment key={lengths.join()}>
                    {index > 0 && ' · '}
                    <span className="dsp-rail-group">
                      {`${lengthLabel(lengths, { range: true })} ${money(price, rails.currency)}`}
                    </span>
                  </React.Fragment>
                ))}
                . Oak, maple or black.
              </p>
            </div>
          )}

          {rails && (
            <ProductDetailLink productId={RAILS_ID}>
              <a
                className="lnd-arrow-link dsp-rails-link"
                onClick={track('rails', 'rail_guide', RAILS_ID)}
              >
                {`Shop ${rails.name}`}
                <PdpIcon name="arrowRight" size={16} />
              </a>
            </ProductDetailLink>
          )}
        </div>
      </Container>
    </section>
  );
};

RailGuide.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      short: PropTypes.string,
      // Print size in inches, short side first.
      size: PropTypes.arrayOf(PropTypes.number).isRequired,
      portrait: PropTypes.number.isRequired,
      landscape: PropTypes.number.isRequired,
    }),
  ).isRequired,
  image: imageShape.isRequired,
  track: PropTypes.func.isRequired,
};

export default RailGuide;
